import { useContextSelector as useCS } from 'use-context-selector';
import { CreateJobPostConfigContext } from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { useGetJobPostById } from 'view/pages/createJobPost/hooks/useGetJobPostById';
import { BackButton, Button, Header, Spinner } from 'view/components';
import {
  CompanyLogoIcon,
  SalaryRangeIcon,
  PositionTypeIcon,
  LocationTypeIcon,
  ExperienceLevelIcon,
  LanguagesIcon,
  HiringNeedIcon,
  EducationLevelIcon,
  DraftGreyIcon,
  DraftRestoreGreyIcon,
  DeleteRedIcon,
  EditPurpleIcon,
} from 'assets';
import {
  FilledRectangleIcon,
  OutlinedRectangleIcon,
} from 'view/pages/profile/TestPage/MultiplySelect';
import { AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGetApplicants } from 'view/pages/candidates/hooks/useGetApplicants';
import CircularProgress from '@mui/material/CircularProgress';
import { getMatchScoreColor } from 'view/pages/profile/components/JobPostsListItem';
import { useUpdateJobPostByStep } from 'view/pages/createJobPost/hooks/useUpdateJobPostByStep';
import { storageService } from 'infrastructure/services/StorageService';
import { TUpdate } from 'domain/types';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { DeleteJobPostModal, MoveJobPostToActiveModal, MoveJobPostToDraftModal } from 'view/modals';
import { showSystemToast } from 'view/components/SystemToast';
import { useStateMachine } from 'little-state-machine';
import { resetForm } from 'view/pages/createJobPost/helpers/resetForm';
import * as S from './styled';

const yearsOfExperienceOptions = [
  {
    value: 'Less than 1 year',
    label: 'Less than 1 year',
    experienceYearsMin: 0,
    experienceYearsMax: 1,
  },
  {
    value: '1-2 years',
    label: '1-2 years',
    experienceYearsMin: 1,
    experienceYearsMax: 2,
  },
  { value: '3-5 years', label: '3-5 years', experienceYearsMin: 3, experienceYearsMax: 5 },
  { value: '6-10 years', label: '6-10 years', experienceYearsMin: 6, experienceYearsMax: 10 },
  {
    value: 'More than 10 years',
    label: 'More than 10 years',
    experienceYearsMin: 10,
    experienceYearsMax: 100,
  },
];

const JobPostCardPreview = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const type: any = params.get('type');
  const jobPostId: any = params.get('jobPostId');
  const [expanded, setExpanded] = useState<string | false>('panel1');

  const { actions } = useStateMachine({
    actions: { resetForm },
  });

  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );

  const updateJobPostData: any = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const update: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const { isLoading } = useGetJobPostById();
  const { updateJobPostForEditing } = useUpdateJobPostByStep('/create-job-post/step1');

  const isMoveToActiveDisabled = useMemo(() => {
    return (
      !createJobPostData?.industry ||
      !createJobPostData?.jobCategory ||
      !createJobPostData?.jobTitle ||
      !createJobPostData?.positionType?.length ||
      !createJobPostData?.experienceYearsMax ||
      !createJobPostData?.experienceYearsMin ||
      !createJobPostData?.rolesAndResponsibilities ||
      !createJobPostData?.company?.id ||
      !createJobPostData?.applicationProcess
    );
  }, [createJobPostData]);

  const {
    isLoading: isAllApplicantsLoading,
    getAllApplicants,
    data: allApplicantsData,
  } = useGetApplicants();

  useEffect(() => {
    if (type && jobPostId && type === 'view') {
      getAllApplicants(jobPostId, 1, 3);
    }
  }, [type, jobPostId]);

  const handleChange = (panel: string) => {
    return (event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };
  };

  return (
    <S.JobPostCardPreviewWrapper>
      <Header />
      <S.JobPostCardPreviewMainWrapper>
        <S.CustomCJPBackButtonBox>
          {createJobPostData?.progressStep && !type ? (
            <BackButton
              text="Back to Create Job Post"
              color="#663497"
              onClick={() => {
                return navigate(`/create-job-post/step${createJobPostData?.progressStep}`, {
                  replace: true,
                });
              }}
            />
          ) : null}
        </S.CustomCJPBackButtonBox>
        <S.JobPostCardPreviewMainBlock>
          {isLoading || !createJobPostData ? (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Spinner variant="outlined" />
            </div>
          ) : (
            <>
              <S.JobPostCardPreviewMainBlockHeader>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <S.JobPostCardPreviewMainBlockHeaderTitle component="h2">
                    {createJobPostData?.jobTitle}
                  </S.JobPostCardPreviewMainBlockHeaderTitle>
                  {type && jobPostId && type === 'view' ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <Button
                        className="red-button hover"
                        variant="outlined"
                        text=""
                        endIcon={<DeleteRedIcon style={{ cursor: 'pointer', flexShrink: 0 }} />}
                        onClick={() => {
                          if (update)
                            update({
                              isOpenDeleteJobPostModal: true,
                              jobPostIdToDelete: jobPostId,
                            });
                        }}
                        disabled={false}
                        style={{
                          minWidth: '45px',
                          minHeight: '45px',
                          padding: 0,
                          gap: 0,
                        }}
                      />
                      {createJobPostData?.postStatus === 'active' ? (
                        <Button
                          variant="outlined"
                          text=""
                          endIcon={
                            <DraftGreyIcon
                              style={{ cursor: 'pointer', flexShrink: 0 }}
                              fill="#663497"
                            />
                          }
                          onClick={() => {
                            if (update)
                              update({
                                isOpenMoveJobPostToDraftModal: true,
                                jobPostIdToDraft: jobPostId,
                              });
                          }}
                          disabled={false}
                          style={{
                            minWidth: '45px',
                            minHeight: '45px',
                            padding: 0,
                            gap: 0,
                          }}
                        />
                      ) : (
                        <Button
                          variant="outlined"
                          text=""
                          endIcon={
                            <DraftRestoreGreyIcon
                              style={{
                                cursor: 'pointer',
                                flexShrink: 0,
                                opacity: isMoveToActiveDisabled ? '0.5' : '1',
                              }}
                              fill={isMoveToActiveDisabled ? null : '#663497'}
                            />
                          }
                          onClick={() => {
                            if (update)
                              update({
                                isOpenMoveJobPostToActiveModal: true,
                                jobPostIdToActive: jobPostId,
                              });
                          }}
                          disabled={isMoveToActiveDisabled}
                          style={{
                            minWidth: '45px',
                            minHeight: '45px',
                            padding: 0,
                            gap: 0,
                          }}
                        />
                      )}

                      <Button
                        variant="outlined"
                        text="Edit"
                        startIcon={
                          <EditPurpleIcon
                            style={{ cursor: 'pointer', flexShrink: 0, marginRight: '5px' }}
                          />
                        }
                        onClick={() => {
                          storageService.setItem('createJobPostId', jobPostId).then((res) => {
                            updateJobPostForEditing({ editStatus: 'edit' }, res);
                            actions.resetForm();
                          });
                        }}
                        disabled={false}
                        style={{
                          minWidth: 'max-content',
                          minHeight: '45px',
                          padding: '0 24px',
                          gap: 0,
                        }}
                      />
                    </div>
                  ) : null}
                </div>

                <S.JobPostCardPreviewMainBlockHeaderCompanyInfo>
                  <S.JobPostCardPreviewMainBlockHeaderCompanyInfoLeft>
                    {createJobPostData?.company?.logo?.link ||
                    createJobPostData?.company?.logoUrl ? (
                      <img
                        src={
                          createJobPostData.company?.logo?.link ||
                          createJobPostData.company?.logoUrl
                        }
                        alt={createJobPostData.company.name}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 12,
                          objectFit: 'cover' as const,
                        }}
                      />
                    ) : (
                      <CompanyLogoIcon />
                    )}
                    <S.CompanyInfoLeftBlock>
                      <S.CompanyInfoLeftBlockTitle>
                        {createJobPostData?.company?.name ||
                          createJobPostData?.companyNameLegacy ||
                          'Company is not provided'}
                      </S.CompanyInfoLeftBlockTitle>
                      {createJobPostData?.location?.name ? (
                        <S.CompanyInfoLeftBlockSubTitle>
                          {createJobPostData?.location?.name},{' '}
                          {createJobPostData?.location?.state?.name}
                        </S.CompanyInfoLeftBlockSubTitle>
                      ) : (
                        <S.CompanyInfoLeftBlockSubTitle>
                          Location is not provided
                        </S.CompanyInfoLeftBlockSubTitle>
                      )}
                    </S.CompanyInfoLeftBlock>
                  </S.JobPostCardPreviewMainBlockHeaderCompanyInfoLeft>
                  <S.JobInfoBlock>
                    <S.JobInfoBlockItem>
                      <p>
                        <span>Industry:</span> {createJobPostData?.industry?.name || 'Not provided'}
                      </p>
                    </S.JobInfoBlockItem>
                    <S.JobInfoBlockItem>
                      <p>
                        <span>Category:</span>{' '}
                        {createJobPostData?.jobCategory?.name || 'Not provided'}
                      </p>
                    </S.JobInfoBlockItem>
                  </S.JobInfoBlock>
                </S.JobPostCardPreviewMainBlockHeaderCompanyInfo>
              </S.JobPostCardPreviewMainBlockHeader>
              {!isAllApplicantsLoading && allApplicantsData?.results?.length > 0 ? (
                <S.ApplicantsContainer>
                  <S.ApplicantsContainerHeading>
                    <S.ApplicantsContainerHeadingNumbers>
                      {allApplicantsData?.total}{' '}
                      {allApplicantsData?.total > 1 ? 'Applicants' : 'Applicant'},{' '}
                      {createJobPostData?.clicksCounter} views
                    </S.ApplicantsContainerHeadingNumbers>
                    <S.ApplicantsContainerHeadingSeeAll
                      onClick={() => {
                        navigate(`/candidates/applicants?jobPostId=${jobPostId}&applicantType=all`);
                      }}
                    >
                      See all Applicants
                    </S.ApplicantsContainerHeadingSeeAll>
                  </S.ApplicantsContainerHeading>
                  <S.ApplicantsContainerCards>
                    {allApplicantsData?.results?.map((el) => {
                      return (
                        <S.ApplicantsContainerCardsItem
                          onClick={() => {
                            navigate(
                              `/candidates/candidate-profile?id=${el?.id}&matchScore=${el?.matchScore || el?.score}`
                            );
                          }}
                        >
                          <div style={{ width: '48px', height: '48px', position: 'relative' }}>
                            <img
                              src={el?.user?.photo?.link}
                              alt="avatar"
                              style={{ width: '48px', height: '48px', borderRadius: '50%' }}
                            />
                            {el?.matchScore || el?.score ? (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                }}
                              >
                                <CircularProgress
                                  variant="determinate"
                                  value={el?.matchScore || el?.score}
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    color: getMatchScoreColor(el?.matchScore || el?.score),
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                          <S.ApplicantsContainerCardsItemTexts>
                            <h4>{el?.jobTitle}</h4>
                            {el?.matchScore ||
                              (el?.score && <p>{el?.matchScore || el?.score}% match</p>)}
                          </S.ApplicantsContainerCardsItemTexts>
                        </S.ApplicantsContainerCardsItem>
                      );
                    })}
                  </S.ApplicantsContainerCards>
                </S.ApplicantsContainer>
              ) : null}
              <S.JobSummaryContainer>
                <S.JobSummaryTitle>Job Summary</S.JobSummaryTitle>
                <S.JobSummaryContent>
                  <S.JobSummaryContentItemBox>
                    <SalaryRangeIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Salary Range
                      </S.JobSummaryContentItemTextBoxTitle>
                      {!createJobPostData?.salaryMin || !createJobPostData?.salaryMax ? (
                        <S.JobSummaryContentItemTextBoxSubTitle>
                          Salary is not provided
                        </S.JobSummaryContentItemTextBoxSubTitle>
                      ) : (
                        <S.JobSummaryContentItemTextBoxSubTitle>
                          ${createJobPostData?.salaryMin?.toLocaleString()} - $
                          {createJobPostData?.salaryMax?.toLocaleString()}
                        </S.JobSummaryContentItemTextBoxSubTitle>
                      )}
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <PositionTypeIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Position Type
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {createJobPostData?.positionType?.length > 0
                          ? createJobPostData?.positionType.map((positionType) => {
                              return `${positionType.name}, `;
                            })
                          : 'Not provided'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <LocationTypeIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Location Type
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {createJobPostData?.jobLocation?.remote && 'Remote, '}
                        {createJobPostData?.jobLocation?.hybrid && 'Hybrid, '}
                        {createJobPostData?.jobLocation?.onSite && 'On Site'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <ExperienceLevelIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Experience Level
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {createJobPostData?.experienceLevel?.name || 'Not provided'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <LanguagesIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Languages
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {createJobPostData?.languages?.length > 0
                          ? createJobPostData?.languages.map((laguage) => {
                              return `${laguage.name}, `;
                            })
                          : 'Not provided'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <HiringNeedIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Immediate Hiring Need
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {createJobPostData?.immHiringNeed ? 'Yes' : 'No'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <EducationLevelIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Education Level
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {createJobPostData?.education?.name || 'Not provided'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                  <S.JobSummaryContentItemBox>
                    <EducationLevelIcon />
                    <S.JobSummaryContentItemTextBox>
                      <S.JobSummaryContentItemTextBoxTitle>
                        Years of Experience
                      </S.JobSummaryContentItemTextBoxTitle>
                      <S.JobSummaryContentItemTextBoxSubTitle>
                        {yearsOfExperienceOptions.filter((item) => {
                          return (
                            item.experienceYearsMin === createJobPostData?.experienceYearsMin &&
                            item.experienceYearsMax === createJobPostData?.experienceYearsMax
                          );
                        })?.[0]?.value || 'No Experience'}
                      </S.JobSummaryContentItemTextBoxSubTitle>
                    </S.JobSummaryContentItemTextBox>
                  </S.JobSummaryContentItemBox>
                </S.JobSummaryContent>
              </S.JobSummaryContainer>
              <S.JobDetailsContainer>
                <S.JobDetailsTitle>Job Details</S.JobDetailsTitle>
                <S.JobDetailsContent>
                  <S.ReusableTextBlock>
                    <S.ReusableTextBlockTitle>Roles and Responsibilities</S.ReusableTextBlockTitle>
                    <S.ReusableTextBlockDesc>
                      {createJobPostData?.rolesAndResponsibilities || 'Not provided'}
                    </S.ReusableTextBlockDesc>
                  </S.ReusableTextBlock>
                  <S.ReusableTextBlock>
                    <S.ReusableTextBlockTitle>Qualifications & Skills</S.ReusableTextBlockTitle>
                    <S.JobDetailsContentQualifications>
                      <S.JobDetailsContentQualificationsItem>
                        <S.JobDetailsContentQualificationsItemTitle>
                          Qualifications
                        </S.JobDetailsContentQualificationsItemTitle>
                        {createJobPostData?.qualifications?.length > 0
                          ? createJobPostData?.qualifications?.map((qual) => {
                              return (
                                <S.JobDetailsContentQualificationsItemBox key={qual.id}>
                                  <S.JobDetailsContentQualificationsItemBoxImportance>
                                    {Array.from({ length: 5 }).map((_, index) => {
                                      return index < qual.importance ? (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <div key={index}>
                                          <FilledRectangleIcon />
                                        </div>
                                      ) : (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <div key={index}>
                                          <OutlinedRectangleIcon />
                                        </div>
                                      );
                                    })}
                                  </S.JobDetailsContentQualificationsItemBoxImportance>
                                  <S.JobDetailsContentQualificationsItemBoxName>
                                    {qual?.qualification.name}
                                  </S.JobDetailsContentQualificationsItemBoxName>
                                </S.JobDetailsContentQualificationsItemBox>
                              );
                            })
                          : 'Not provided'}
                      </S.JobDetailsContentQualificationsItem>
                      <S.JobDetailsContentQualificationsItem>
                        <S.JobDetailsContentQualificationsItemTitle>
                          Technical Skills
                        </S.JobDetailsContentQualificationsItemTitle>
                        {createJobPostData?.skills?.length > 0 &&
                        createJobPostData?.skills?.filter((item) => {
                          return item.skill.group === 'Hard Skills';
                        }).length > 0
                          ? createJobPostData?.skills
                              ?.filter((item) => {
                                return item.skill.group === 'Hard Skills';
                              })
                              ?.map((hardSkill) => {
                                return (
                                  <S.JobDetailsContentQualificationsItemBox key={hardSkill.id}>
                                    <S.JobDetailsContentQualificationsItemBoxImportance>
                                      {Array.from({ length: 5 }).map((_, index) => {
                                        return index < hardSkill.importance ? (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <div key={index}>
                                            <FilledRectangleIcon />
                                          </div>
                                        ) : (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <div key={index}>
                                            <OutlinedRectangleIcon />
                                          </div>
                                        );
                                      })}
                                    </S.JobDetailsContentQualificationsItemBoxImportance>
                                    <S.JobDetailsContentQualificationsItemBoxName>
                                      {hardSkill?.skill?.name}
                                    </S.JobDetailsContentQualificationsItemBoxName>
                                  </S.JobDetailsContentQualificationsItemBox>
                                );
                              })
                          : 'Not provided'}
                      </S.JobDetailsContentQualificationsItem>
                      <S.JobDetailsContentQualificationsItem>
                        <S.JobDetailsContentQualificationsItemTitle>
                          Soft Skills
                        </S.JobDetailsContentQualificationsItemTitle>
                        {createJobPostData?.skills?.length > 0 &&
                        createJobPostData?.skills?.filter((item) => {
                          return item.skill.group === 'Soft Skills';
                        }).length > 0
                          ? createJobPostData?.skills
                              ?.filter((item) => {
                                return item.skill.group === 'Soft Skills';
                              })
                              ?.map((softSkill) => {
                                return (
                                  <S.JobDetailsContentQualificationsItemBox key={softSkill.id}>
                                    <S.JobDetailsContentQualificationsItemBoxImportance>
                                      {Array.from({ length: 5 }).map((_, index) => {
                                        return index < softSkill.importance ? (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <div key={index}>
                                            <FilledRectangleIcon />
                                          </div>
                                        ) : (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <div key={index}>
                                            <OutlinedRectangleIcon />
                                          </div>
                                        );
                                      })}
                                    </S.JobDetailsContentQualificationsItemBoxImportance>
                                    <S.JobDetailsContentQualificationsItemBoxName>
                                      {softSkill?.skill?.name}
                                    </S.JobDetailsContentQualificationsItemBoxName>
                                  </S.JobDetailsContentQualificationsItemBox>
                                );
                              })
                          : 'Not provided'}
                      </S.JobDetailsContentQualificationsItem>
                    </S.JobDetailsContentQualifications>
                  </S.ReusableTextBlock>
                </S.JobDetailsContent>
              </S.JobDetailsContainer>
              <S.CompanyContainer>
                <S.CompanyTitle>Company</S.CompanyTitle>
                <S.CompanyContent>
                  <S.ReusableTextBlock>
                    <S.ReusableTextBlockTitle>Overview</S.ReusableTextBlockTitle>
                    <S.ReusableTextBlockDesc>
                      {createJobPostData?.companyOverview || 'Not provided'}
                    </S.ReusableTextBlockDesc>
                  </S.ReusableTextBlock>
                  <S.ReusableTextBlock>
                    <S.ReusableTextBlockTitle>Benefits</S.ReusableTextBlockTitle>
                    <S.ReusableTextBlockDesc>
                      {createJobPostData?.companyBenefits || 'Not provided'}
                    </S.ReusableTextBlockDesc>
                  </S.ReusableTextBlock>
                  <S.ReusableTextBlock>
                    <S.ReusableTextBlockTitle>Diversity and Inclusion</S.ReusableTextBlockTitle>
                    <S.ReusableTextBlockDesc>
                      {createJobPostData?.diversityStatement || 'Not provided'}
                    </S.ReusableTextBlockDesc>
                  </S.ReusableTextBlock>
                </S.CompanyContent>
              </S.CompanyContainer>
              {createJobPostData?.faq?.length > 0 ? (
                <S.FAQContainer>
                  <S.FAQTitle>FAQs</S.FAQTitle>
                  <S.FAQContent>
                    <S.FAQWrapper>
                      {createJobPostData?.faq?.length > 0
                        ? createJobPostData?.faq?.map((item, index) => {
                            const { answer, question } = item;
                            const expandedId = `panel${index + 1}`;
                            const ariaControls = `panel${index + 1}bh-content`;
                            const summaryId = `panel${index + 1}bh-header`;
                            return (
                              <S.CustomAccordion
                                expanded={expanded === expandedId}
                                onChange={handleChange(expandedId)}
                                key={expandedId}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  aria-controls={ariaControls}
                                  id={summaryId}
                                >
                                  <S.AccordionTitle component="span">{question}</S.AccordionTitle>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <S.AccordionDesc>{answer}</S.AccordionDesc>
                                </AccordionDetails>
                              </S.CustomAccordion>
                            );
                          })
                        : null}
                    </S.FAQWrapper>
                  </S.FAQContent>
                </S.FAQContainer>
              ) : null}

              {createJobPostData?.applicationProcess ? (
                <S.ApplicationProcessContainer>
                  <S.ApplicationProcessTitle>Application Process</S.ApplicationProcessTitle>
                  <S.ApplicationProcessContent>
                    <S.ReusableTextBlock>
                      <S.ReusableTextBlockDesc>
                        {createJobPostData?.applicationProcess}
                      </S.ReusableTextBlockDesc>
                    </S.ReusableTextBlock>
                  </S.ApplicationProcessContent>
                </S.ApplicationProcessContainer>
              ) : null}

              <S.Footer>
                <S.JobPostCardPreviewMainBlockHeaderCompanyInfoLeft>
                  {createJobPostData?.company?.logo?.link || createJobPostData?.company?.logoUrl ? (
                    <img
                      src={
                        createJobPostData.company?.logo?.link || createJobPostData.company?.logoUrl
                      }
                      alt={createJobPostData.company.name}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 12,
                        objectFit: 'cover' as const,
                      }}
                    />
                  ) : (
                    <CompanyLogoIcon />
                  )}
                  <S.CompanyInfoLeftBlock>
                    <S.CompanyInfoLeftBlockTitle>
                      {createJobPostData?.company?.name ||
                        createJobPostData?.companyNameLegacy ||
                        'Not provided'}
                    </S.CompanyInfoLeftBlockTitle>
                    {createJobPostData?.location?.name ? (
                      <S.CompanyInfoLeftBlockSubTitle>
                        {createJobPostData?.location?.name},{' '}
                        {createJobPostData?.location?.state?.name}
                      </S.CompanyInfoLeftBlockSubTitle>
                    ) : (
                      <S.CompanyInfoLeftBlockSubTitle>
                        Location is not provided
                      </S.CompanyInfoLeftBlockSubTitle>
                    )}
                  </S.CompanyInfoLeftBlock>
                </S.JobPostCardPreviewMainBlockHeaderCompanyInfoLeft>
              </S.Footer>
            </>
          )}
        </S.JobPostCardPreviewMainBlock>
      </S.JobPostCardPreviewMainWrapper>
      <MoveJobPostToDraftModal
        successFunction={() => {
          updateJobPostData({
            createJobPostData: { ...createJobPostData, postStatus: 'draft' },
          });
          showSystemToast(
            'success',
            'Job post moved to draft.',
            'It’s now hidden from job seekers. You can edit and repost it anytime.'
          );
        }}
      />
      <MoveJobPostToActiveModal
        successFunction={() => {
          updateJobPostData({
            createJobPostData: { ...createJobPostData, postStatus: 'active' },
          });
          showSystemToast(
            'success',
            'Job post moved to active.',
            'It’s now shown for job seekers. You can edit and repost it anytime.'
          );
        }}
      />
      <DeleteJobPostModal
        successFunction={() => {
          navigate('/candidates/my-job-posts?postStatus=active', { replace: true });
          showSystemToast(
            'success',
            'Job post deleted successfully.',
            'This job is no longer visible to job seekers.'
          );
        }}
      />
    </S.JobPostCardPreviewWrapper>
  );
};

export default JobPostCardPreview;
